import { useCallback, useEffect, useMemo, useState } from 'react';

import { ladeBediener } from '../bediener';
import type { KassenApi, Termin, TerminEingabe, TerminStatus } from '../api';
import { aktuelleSchnellwahl, positionAus } from '../schnellwahl';
import {
  lageImRaster,
  tagText,
  tagVerschieben,
  uhrzeitText,
  wienerMinuten,
  wienerTag,
  wienerZeitpunkt,
} from '../termine';
import { formatiereEuro, type Position } from '../warenkorb';

const START_STUNDE = 8;
const END_STUNDE = 20;
const ZEILEN = (END_STUNDE - START_STUNDE) * 4;
const OHNE = '– ohne –';

const STATUS_TEXT: Record<TerminStatus, string> = {
  geplant: 'geplant',
  erschienen: 'erschienen',
  abgesagt: 'abgesagt',
  nicht_erschienen: 'nicht erschienen',
};

interface Formular {
  id: number | null;
  uhrzeit: string;
  dauerMin: number;
  kunde: string;
  telefon: string;
  leistung: string;
  bediener: string;
  notiz: string;
  status: TerminStatus;
}

/**
 * Terminbuch (Modul „termine“): Tagesansicht mit einer Spalte je Mitarbeiter,
 * 08–20 Uhr in Viertelstunden. Freies Feld antippen legt einen Termin an,
 * Termin antippen bearbeitet ihn. „Kassieren“ legt die Leistung in den Bon.
 */
export function Termine(props: {
  api: KassenApi;
  kassenId: string;
  onKassieren: (positionen: Position[]) => void;
}) {
  const { api, kassenId } = props;
  const [tag, setTag] = useState(() => wienerTag(new Date()));
  const [termine, setTermine] = useState<Termin[]>([]);
  const [form, setForm] = useState<Formular | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const kacheln = useMemo(() => aktuelleSchnellwahl(localStorage, kassenId).kacheln, [kassenId]);
  /** Mitarbeiter aus der Bedienerverwaltung am Server; ohne sie die lokale Liste des Geräts. */
  const [mitarbeiter, setMitarbeiter] = useState<string[]>([]);
  useEffect(() => {
    let aktuell = true;
    api
      .bedienerListe(kassenId)
      .then((b) => {
        if (aktuell && b.verwaltet) {
          setMitarbeiter(b.bediener.map((x) => x.name));
        }
      })
      .catch(() => undefined);
    return () => {
      aktuell = false;
    };
  }, [api, kassenId]);

  const spalten = useMemo(() => {
    const namen = mitarbeiter.length > 0 ? mitarbeiter : ladeBediener(localStorage, kassenId).liste;
    const ausTerminen = termine
      .map((t) => t.bediener)
      .filter((b): b is string => b !== null && !namen.includes(b));
    const alle = [...namen, ...new Set(ausTerminen)];
    return alle.length === 0 || termine.some((t) => t.bediener === null) ? [...alle, OHNE] : alle;
  }, [mitarbeiter, kassenId, termine]);

  const laden = useCallback(async () => {
    try {
      setTermine((await api.termine(kassenId, tag)).termine);
      setFehler(null);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, kassenId, tag]);
  useEffect(() => {
    void laden();
  }, [laden]);

  function neu(minuten: number, bediener: string): void {
    setForm({
      id: null,
      uhrzeit: uhrzeitText(minuten),
      dauerMin: 30,
      kunde: '',
      telefon: '',
      leistung: '',
      bediener: bediener === OHNE ? '' : bediener,
      notiz: '',
      status: 'geplant',
    });
  }

  function bearbeiten(t: Termin): void {
    setForm({
      id: t.id,
      uhrzeit: uhrzeitText(wienerMinuten(new Date(t.beginn))),
      dauerMin: t.dauerMin,
      kunde: t.kunde,
      telefon: t.telefon ?? '',
      leistung: t.leistung ?? '',
      bediener: t.bediener ?? '',
      notiz: t.notiz ?? '',
      status: t.status,
    });
  }

  function kachelZu(leistung: string) {
    return kacheln.find((k) => k.bezeichnung === leistung.trim());
  }

  async function speichern(f: Formular, status = f.status): Promise<Termin | null> {
    if (f.kunde.trim() === '') {
      setFehler('Kunde angeben');
      return null;
    }
    setLaeuft(true);
    try {
      const k = kachelZu(f.leistung);
      const eingabe: TerminEingabe = {
        beginn: wienerZeitpunkt(tag, f.uhrzeit).toISOString(),
        dauerMin: f.dauerMin,
        kunde: f.kunde,
        telefon: f.telefon === '' ? null : f.telefon,
        leistung: f.leistung === '' ? null : f.leistung,
        artikelId: k?.artikelId ?? null,
        bediener: f.bediener === '' ? null : f.bediener,
        notiz: f.notiz === '' ? null : f.notiz,
        status,
      };
      const t = await api.terminSpeichern(kassenId, f.id, eingabe);
      setFehler(null);
      await laden();
      return t;
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setLaeuft(false);
    }
  }

  async function kassieren(f: Formular): Promise<void> {
    const k = kachelZu(f.leistung);
    const t = await speichern(f, 'erschienen');
    if (t === null) {
      return;
    }
    setForm(null);
    props.onKassieren(k === undefined ? [] : [positionAus(k, false)]);
  }

  const jetztMin = wienerMinuten(new Date()) - START_STUNDE * 60;
  const istHeute = tag === wienerTag(new Date());

  return (
    <>
      <div className="karte">
        <div className="zeile" style={{ alignItems: 'center' }}>
          <button
            type="button"
            className="taste zweit"
            onClick={() => {
              setTag(tagVerschieben(tag, -1));
            }}
          >
            ←
          </button>
          <strong className="termin-tag">{tagText(tag)}</strong>
          <button
            type="button"
            className="taste zweit"
            onClick={() => {
              setTag(tagVerschieben(tag, 1));
            }}
          >
            →
          </button>
          {!istHeute && (
            <button
              type="button"
              className="taste zweit"
              onClick={() => {
                setTag(wienerTag(new Date()));
              }}
            >
              Heute
            </button>
          )}
          <span className="hinweis">
            {termine.filter((t) => t.status !== 'abgesagt').length} Termine
          </span>
        </div>
        {fehler !== null && <p className="fehler">{fehler}</p>}
      </div>

      <div className="karte termin-karte">
        <div
          className="termin-raster"
          style={{
            gridTemplateColumns: `3.5rem repeat(${String(spalten.length)}, minmax(9rem, 1fr))`,
          }}
        >
          <div className="termin-ecke" />
          {spalten.map((s) => (
            <div key={s} className="termin-kopf">
              {s}
            </div>
          ))}
          <div className="termin-zeiten" style={{ gridRow: `2 / span ${String(ZEILEN)}` }}>
            {Array.from({ length: END_STUNDE - START_STUNDE }, (_, i) => (
              <span key={i}>{uhrzeitText((START_STUNDE + i) * 60)}</span>
            ))}
          </div>
          {spalten.map((s, si) => (
            <div
              key={s}
              className="termin-spalte"
              style={{ gridColumn: String(si + 2), gridRow: `2 / span ${String(ZEILEN)}` }}
            >
              {Array.from({ length: ZEILEN }, (_, z) => (
                <button
                  key={z}
                  type="button"
                  className={`termin-feld${z % 4 === 0 ? ' volle' : ''}`}
                  aria-label={`${s} ${uhrzeitText(START_STUNDE * 60 + z * 15)} neuer Termin`}
                  onClick={() => {
                    neu(START_STUNDE * 60 + z * 15, s);
                  }}
                />
              ))}
              {istHeute && jetztMin > 0 && jetztMin < ZEILEN * 15 && (
                <div
                  className="termin-jetzt"
                  style={{ top: `${String((jetztMin / (ZEILEN * 15)) * 100)}%` }}
                />
              )}
              {termine
                .filter((t) => (t.bediener ?? OHNE) === s)
                .map((t) => {
                  const lage = lageImRaster(
                    new Date(t.beginn),
                    t.dauerMin,
                    START_STUNDE,
                    END_STUNDE,
                  );
                  if (lage === null) {
                    return null;
                  }
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={`termin ${t.status}`}
                      style={{
                        top: `${String((lage.zeile / ZEILEN) * 100)}%`,
                        height: `${String((lage.zeilen / ZEILEN) * 100)}%`,
                      }}
                      onClick={() => {
                        bearbeiten(t);
                      }}
                    >
                      <strong>
                        {uhrzeitText(wienerMinuten(new Date(t.beginn)))} {t.kunde}
                      </strong>
                      {/* Status in derselben Zeile wie die Leistung – kurze Termine haben nur zwei Zeilen Platz. */}
                      {(t.leistung !== null || t.status !== 'geplant') && (
                        <span>
                          {t.leistung}
                          {t.status !== 'geplant' && (
                            <small>
                              {t.leistung === null ? '' : ' · '}
                              {STATUS_TEXT[t.status]}
                            </small>
                          )}
                        </span>
                      )}
                    </button>
                  );
                })}
            </div>
          ))}
        </div>
      </div>

      {form !== null && (
        <div className="zahlung-dialog">
          <form
            className="karte termin-dialog"
            onSubmit={(e) => {
              e.preventDefault();
              void speichern(form).then((t) => {
                if (t !== null) {
                  setForm(null);
                }
              });
            }}
          >
            <h2>{form.id === null ? 'Neuer Termin' : 'Termin'}</h2>
            <div className="zeile">
              <label>
                Uhrzeit
                <input
                  type="time"
                  step={300}
                  value={form.uhrzeit}
                  onChange={(e) => {
                    setForm({ ...form, uhrzeit: e.target.value });
                  }}
                />
              </label>
              <label>
                Dauer
                <select
                  value={form.dauerMin}
                  onChange={(e) => {
                    setForm({ ...form, dauerMin: Number(e.target.value) });
                  }}
                >
                  {[15, 30, 45, 60, 75, 90, 120, 150, 180, 240].map((m) => (
                    <option key={m} value={m}>
                      {m < 60 ? `${String(m)} min` : `${String(m / 60).replace('.', ',')} h`}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Mitarbeiter
                <select
                  value={form.bediener}
                  onChange={(e) => {
                    setForm({ ...form, bediener: e.target.value });
                  }}
                >
                  <option value="">{OHNE}</option>
                  {spalten
                    .filter((s) => s !== OHNE)
                    .map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                </select>
              </label>
            </div>
            <div className="zeile">
              <label style={{ flex: 2 }}>
                Kunde
                <input
                  value={form.kunde}
                  maxLength={120}
                  autoFocus={form.id === null}
                  onChange={(e) => {
                    setForm({ ...form, kunde: e.target.value });
                  }}
                />
              </label>
              <label style={{ flex: 1 }}>
                Telefon
                <input
                  type="tel"
                  value={form.telefon}
                  maxLength={40}
                  onChange={(e) => {
                    setForm({ ...form, telefon: e.target.value });
                  }}
                />
              </label>
            </div>
            <label>
              Leistung
              <input
                list="termin-leistungen"
                value={form.leistung}
                maxLength={200}
                placeholder="z. B. Haarschnitt"
                onChange={(e) => {
                  setForm({ ...form, leistung: e.target.value });
                }}
              />
              <datalist id="termin-leistungen">
                {kacheln.map((k) => (
                  <option key={`${k.gruppe}-${k.bezeichnung}`} value={k.bezeichnung}>
                    {formatiereEuro(k.bruttoCent)} €
                  </option>
                ))}
              </datalist>
            </label>
            <label>
              Notiz
              <input
                value={form.notiz}
                maxLength={500}
                onChange={(e) => {
                  setForm({ ...form, notiz: e.target.value });
                }}
              />
            </label>
            {form.id !== null && <p className="hinweis">Status: {STATUS_TEXT[form.status]}</p>}
            <div className="zeile">
              <button type="submit" className="taste" disabled={laeuft}>
                Speichern
              </button>
              {form.id !== null && (
                <button
                  type="button"
                  className="taste"
                  disabled={laeuft}
                  title={
                    kachelZu(form.leistung) === undefined
                      ? 'Leistung nicht im Artikelstamm – an der Kasse wählen'
                      : `${form.leistung} in den Bon legen`
                  }
                  onClick={() => void kassieren(form)}
                >
                  Kassieren
                </button>
              )}
              {form.id !== null && form.status !== 'nicht_erschienen' && (
                <button
                  type="button"
                  className="taste zweit"
                  disabled={laeuft}
                  onClick={() =>
                    void speichern(form, 'nicht_erschienen').then((t) => {
                      if (t !== null) {
                        setForm(null);
                      }
                    })
                  }
                >
                  Nicht erschienen
                </button>
              )}
              {form.id !== null && form.status !== 'abgesagt' && (
                <button
                  type="button"
                  className="taste zweit"
                  disabled={laeuft}
                  onClick={() =>
                    void speichern(form, 'abgesagt').then((t) => {
                      if (t !== null) {
                        setForm(null);
                      }
                    })
                  }
                >
                  Absagen
                </button>
              )}
              {form.id !== null && (
                <button
                  type="button"
                  className="taste zweit gefahr"
                  disabled={laeuft}
                  onClick={() => {
                    const id = form.id;
                    if (id === null || !window.confirm('Termin endgültig löschen?')) {
                      return;
                    }
                    void api
                      .terminLoeschen(kassenId, id)
                      .then(async () => {
                        setForm(null);
                        await laden();
                      })
                      .catch((e: unknown) => {
                        setFehler(e instanceof Error ? e.message : String(e));
                      });
                  }}
                >
                  Löschen
                </button>
              )}
              <button
                type="button"
                className="taste zweit"
                onClick={() => {
                  setForm(null);
                  setFehler(null);
                }}
              >
                Abbrechen
              </button>
            </div>
            {fehler !== null && <p className="fehler">{fehler}</p>}
          </form>
        </div>
      )}
    </>
  );
}
